import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom'
import { LayoutDashboard, FileText, LogOut, TrendingUp, Car, MapPin, Shield, Users } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import DAFDashboard from './pages/DAFDashboard'
import DAFSinistres from './pages/DAFSinistres'
import DAFSinistreDetail from './pages/DAFSinistreDetail'
import DAFPolices from './pages/DAFPolices'
import VehiculesPage from '../c2/pages/VehiculesPage'
import SitesPage from '../c2/pages/SitesPage'
import SuiviChauffeursTab from '../c2/pages/SuiviChauffeursTab'

const LOGO_WHITE = '/logo-white.png'

const NAV = [
  { to: '/daf/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/daf/sinistres', icon: FileText,         label: 'Sinistres' },
  { to: '/daf/polices',   icon: Shield,            label: 'Polices & S/P' },
  { to: '/daf/vehicules', icon: Car,               label: 'Vehicules' },
  { to: '/daf/sites',     icon: MapPin,            label: 'Sites'     },
]

export default function DAFApp() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const tenantId = profile?.tenant_id
  const [hasFlotte, setHasFlotte] = useState(null)

  useEffect(() => {
    if (!tenantId) return
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/polices?tenant_id=${tenantId}`)
      .then(r => r.json())
      .then(d => setHasFlotte((d.polices || []).some(p => p.type_police === 'flotte' && p.statut !== 'resiliee')))
      .catch(() => setHasFlotte(false))
  }, [tenantId])

  const navItems = hasFlotte
    ? [...NAV.slice(0, 3), { to: '/daf/chauffeurs', icon: Users, label: 'Chauffeurs' }, ...NAV.slice(3)]
    : NAV

  async function handleLogout() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className="w-[240px] min-h-screen flex flex-col shrink-0"
        style={{ background: '#064E3B' }}
        data-testid="daf-sidebar"
      >
        <div className="px-4 py-4 border-b border-white/10">
          <img 
            src={LOGO_WHITE} 
            alt="C2 Assurances" 
            className="h-7 w-auto"
            data-testid="daf-logo"
          />
          <p className="text-xs text-emerald-300 mt-1.5 ml-0.5">Direction Financière</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`daf-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-white/20 text-white font-semibold'
                    : 'text-emerald-200 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-4 border-t border-white/10 pt-3">
          <div className="px-3 py-2">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-7 h-7 rounded-full bg-emerald-400/30 flex items-center justify-center text-emerald-300 text-xs font-bold">
                {profile?.prenom?.[0]}{profile?.nom?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white truncate">
                  {profile?.prenom} {profile?.nom}
                </p>
                <p className="text-xs text-emerald-300 truncate">{profile?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="daf-logout-button"
              className="flex items-center gap-2 text-xs text-emerald-300 hover:text-red-400 transition-colors"
            >
              <LogOut size={13} />
              Déconnexion
            </button>
          </div>
          <p className="text-center text-[10px] text-emerald-400/40 pt-2">Propriété de C2 Assurances</p>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DAFDashboard />} />
          <Route path="sinistres" element={<DAFSinistres />} />
          <Route path="sinistre/:id" element={<DAFSinistreDetail />} />
          <Route path="polices"   element={<DAFPolices />} />
          <Route path="chauffeurs" element={
            hasFlotte === null ? <div className="p-10 text-sm text-slate-400">Chargement...</div>
              : hasFlotte ? <SuiviChauffeursTab tenantId={tenantId} sinistreBasePath="/daf/sinistre" />
              : <Navigate to="/daf/dashboard" replace />
          } />
          <Route path="vehicules" element={<VehiculesPage tenants={tenantId ? [{ id: tenantId, nom: profile?.tenant_nom || '' }] : []} readOnly={true} forceTenantId={tenantId} />} />
          <Route path="sites"     element={<SitesPage tenants={tenantId ? [{ id: tenantId, nom: profile?.tenant_nom || '' }] : []} readOnly={true} forceTenantId={tenantId} />} />
          <Route path="*"         element={<Navigate to="/daf/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  )
}
